import { ClaimRequest } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import SimpleList from '@platform-ui-common/components/SimpleList/index.js';
import { SimpleListItem } from '@platform-ui-common/components/SimpleList/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import Color, { TextColors } from '@platform-ui-common/utilities/Color.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { useHistory } from 'react-router';

function Primer(): JSX.Element {
  const history = useHistory();
  const { name } = usePartner();
  const { fulfillment, setPayload, setMethod } = useClaim<ClaimRequest>();
  const { user } = useAuth();

  const onReset = (): void => {
    setPayload(null);
    setMethod(null);
    history.push(makeRoute(ClaimRoutes.PAYMENT_METHOD, { fulfillmentId: fulfillment.id }));
  };

  const onClick = (): void => {
    history.push(
      makeRoute(ClaimRoutes.ZELLE_EMAIL_CONFIRMATION, { fulfillmentId: fulfillment.id }),
    );
  };

  const bullets = [
    'Login to your banking institution (e.g. JPMorgan Chase, Bank of America, Wells Fargo, etc.)',
    <>
      Confirm your {name} email address, <strong>{user?.core?.email}</strong>, is connected to your
      Zelle account.
    </>,
    <>
      If your email address, <strong>{user?.core?.email}</strong>, is not connected to your Zelle
      account, you will need to add it to your Zelle account via your banking institution. You may
      add multiple emails to your account or update your email address in your banking
      institution&apos;s account settings by selecting &quot;Add new email or mobile number.&quot;
    </>,
  ];

  useCustomPageTitle(`Is your ${name} email address connected to your Zelle account?`);

  return (
    <div className="w-full flex flex-col items-center text-center gap-8">
      <div className="w-3/4 flex flex-col">
        <Typography variant="h1">
          Is your {name} email address connected to your Zelle account?
        </Typography>
      </div>
      <div className="flex flex-col lg:w-3/4 gap-y-6">
        <Typography variant="largeBody">
          Please confirm this by following the instructions below:
        </Typography>
        <div className="flex text-left">
          <SimpleList space type="number">
            {bullets.map((bullet, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: constant array
              <SimpleListItem key={`item-${idx}`}>{bullet}</SimpleListItem>
            ))}
          </SimpleList>
        </div>
        <div className="flex flex-col gap-2">
          <img
            src="/assets/zelle/zelle_example.png"
            alt="Example: Bank of America's Zelle Settings Page to add a new email"
            className="w-full h-auto object-contain drop-shadow-md"
          />
          <span className={`text-xs text-start ${TextColors[Color.TextSecondary]}`}>
            Example: Bank of America&apos;s Zelle Settings Page to add a new email
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-y-4 items-center">
        <Button onClick={onClick}>Yes, My Zelle Account Is Updated</Button>
        <Button onClick={onReset} variant="outline">
          Select Other Payment Method
        </Button>
      </div>
    </div>
  );
}

export default Primer;
